import { Pipe, PipeTransform } from '@angular/core'
import { FormattableMoment, MomentConvertible, toMoment } from '../../util'

/**
 * Formats the given date to a human-readable "X time ago" or "X from now" string.
 *
 * ## Example usage
 *
 * Please see the [style guide](https://storybook.ftrdev.com) for example usage.
 *
 * ## Live updating
 *
 * This pipe will _not_ automatically update the "time ago" string whenever some time passes. This
 * could be accomplished by setting an inner observable in the pipe which triggers change detection
 * every X seconds (`changeDetectorRef.markForCheck()`) and causes the pipe to re-render its
 * value, however since this pipe could potentially be used many times on a single page (i.e. for
 * every item in a list) it would leak observables and handlers and could lead to bad performance.
 *
 * If you want to achieve live updates, the recommended approach would be to create a single "current time"
 * observable in the component that uses this pipe, and then pass it in the `now` property in this
 * pipe's `options` arg - this way there'd only be one observable that all pipes get their updates from,
 * and no manual change detection overrides would be required.
 *
 * `EveryMinuteObservableFactory` might be useful if you're trying to do this.
 */
@Pipe({ name: 'ftrTimeAgo', standalone: true })
export class TimeAgoPipe implements PipeTransform {
  transform(value: MomentConvertible, now = new Date(), hideSuffix = false): string {
    if (!value) {
      return ''
    }

    let valueAsMoment: FormattableMoment
    let nowAsMoment: FormattableMoment

    try {
      valueAsMoment = toMoment(value)
      nowAsMoment = toMoment(now)
      return valueAsMoment.moment.from(nowAsMoment.moment, hideSuffix)
    } catch (error) {
      console.warn({ message: 'Error converting date to moment in TimeAgoPipe', error })
      return ''
    }
  }
}
